import { useEffect, useMemo, useRef } from 'react';
import type { MemberSubsetDraft } from 'src/components/star-grid/hooks/subset-editor/useMemberSubsetDraft';
import { NO_ROWS } from 'src/components/star-grid/memberSubsetConstants';
import type { PnlHierarchyMember, PnlSubsetViewEntry } from 'src/components/star-grid/starGridTypes';
import {
  allPositionKeys,
  flatMemberRows,
  indexMemberTree,
  searchMemberRows,
  visibleMemberRows
} from 'src/components/star-grid/utils/subset-editor/memberSubset';
import { selectionChainLabels } from 'src/components/star-grid/utils/subset-editor/memberChain';
import { annotateDrillable, materialiseRollups } from 'src/components/star-grid/utils/subset-editor/memberSubsetEntries';
import {
  buildGridRows,
  distinctMemberCount,
  initiallyExpandedUids,
  membersNotInView
} from 'src/components/star-grid/utils/subset-editor/memberSubsetEditor';
import { viewEntries, viewExpandedKeys } from 'src/components/star-grid/utils/subset-editor/savedSubsetViews';

/**
 * The row pipeline — hierarchy or saved view → Keep scope → splices → removals → search → the grid's rows —
 * and what the ticks amount to. Membership is always what is ticked, never what an operation is showing.
 */
export const useSubsetRows = (
  labelled: PnlHierarchyMember[],
  viewRows: readonly PnlSubsetViewEntry[] | null,
  draft: MemberSubsetDraft,
  search: string,
  showAll: (flat: boolean, expanded?: readonly string[]) => void
) => {
  const index = useMemo(() => indexMemberTree(labelled), [labelled]);
  const isSearching = search.trim().length > 0;

  // Each stored row gets its rollup from the hierarchy, collapsed, so operations see a view as they see browsing.
  const savedEntries = useMemo(
    () => (viewRows === null ? null : materialiseRollups(viewEntries(viewRows, index), index, new Set(viewExpandedKeys(viewRows, index)))),
    [viewRows, index]
  );

  /*
   * `showAll` both seeds the expansion and bumps `nonce`, which is what makes the grid re-read the seed.
   * Once per loaded view: `index` also changes on every alias relabel, and re-running `showAll` then would
   * reset the draft and discard any Keep, Remove or Parent applied to the view since.
   */
  const restoredFor = useRef<readonly PnlSubsetViewEntry[] | null>(null);
  useEffect(() => {
    if (viewRows === null || index.size === 0 || restoredFor.current === viewRows) return;
    restoredFor.current = viewRows;
    const expanded = viewExpandedKeys(viewRows, index);
    if (expanded.length > 0) showAll(false, expanded);
  }, [viewRows, index, showAll]);

  // Independent of expansion: the grid owns expand/collapse over a fixed row set, and rebuilding per toggle flickered.
  const treeRows = useMemo(() => visibleMemberRows(labelled, allPositionKeys(index)), [index, labelled]);
  // `All` emits one level past what is open, so it alone depends on expansion.
  const flatRows = useMemo(() => (draft.flat ? flatMemberRows(index, draft.expanded) : NO_ROWS), [draft.flat, index, draft.expanded]);

  const gridRows = useMemo(
    () =>
      buildGridRows({
        flat: draft.flat,
        flatRows,
        treeRows,
        savedEntries,
        scope: draft.scope,
        ops: draft.ops,
        excluded: draft.excluded,
        index
      }),
    [draft.flat, flatRows, treeRows, savedEntries, draft.scope, draft.ops, index, draft.excluded]
  );

  const { rows: searchRows, expand } = useMemo(() => searchMemberRows(gridRows, search), [gridRows, search]);
  // After the search, because a search can filter a row's children away.
  const displayRows = useMemo(() => annotateDrillable(searchRows, index), [searchRows, index]);

  // Pinned to `nonce` on purpose: the grid reads its seed once per mount, and a live Set flickered on every expand.
  const expandedAtMount = useMemo(() => draft.expanded, [draft.nonce]);
  const initiallyExpanded = useMemo(
    () => (isSearching ? expand : initiallyExpandedUids(displayRows, expandedAtMount)),
    [isSearching, expand, displayRows, expandedAtMount]
  );

  const memberKeys = useMemo(() => [...draft.checked], [draft.checked]);
  const chain = useMemo(() => selectionChainLabels(index, memberKeys), [index, memberKeys]);
  const selectedMembers = useMemo(() => distinctMemberCount(index, memberKeys), [index, memberKeys]);
  // Read from `gridRows`, so neither the search nor a collapsed rollup counts as out of view.
  const selectedNotInView = useMemo(() => membersNotInView(index, memberKeys, gridRows), [index, memberKeys, gridRows]);

  return {
    index,
    isSearching,
    gridRows,
    displayRows,
    initiallyExpanded,
    memberKeys,
    chain,
    selectedMembers,
    selectedNotInView,
    isUnrestricted: selectedMembers === 0
  };
};
